import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TwoFactorStep, TwoFactorEnroll } from "@/components/TwoFactor";

const BG = "https://images.unsplash.com/photo-1638184984605-af1f05249a56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjB0ZWNobmljYWwlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NjIyMzM3Nnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, persistSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(null);   // { mode: "2fa" | "enroll", token }

  const finish = (data) => {
    persistSession(data);
    navigate("/");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.mfa_required) setStep({ mode: "2fa", token: data.mfa_token });
      else if (data.enrollment_required) setStep({ mode: "enroll", token: data.mfa_token });
      else navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-12 sm:px-16 lg:px-24">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2">
            <img src="/icon-192.png" alt="" className="h-9 w-9" />
            <span className="font-display text-xl font-semibold tracking-tight">LocalPro Beacon</span>
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Welcome back</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to your visibility control panel.</p>
          {!step && new URLSearchParams(window.location.search).get("ended") && (
            <p className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-600 dark:text-amber-400"
              data-testid="session-ended-notice">
              That session was signed out — a password change, a two-step reset or a "log out everywhere"
              ends every other session. Please sign in again.
            </p>
          )}

          {step?.mode === "2fa" && <TwoFactorStep mfaToken={step.token} onDone={finish} />}
          {step?.mode === "enroll" && <TwoFactorEnroll mfaToken={step.token} onDone={finish} />}

          {!step && <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="login-submit" type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
          </form>}
          {!step && (
            <p className="mt-6 text-sm text-muted-foreground">
              Accounts are created by invitation. Ask an administrator for an invite link.
            </p>
          )}
        </motion.div>
      </div>
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/60" />
        <div className="absolute bottom-0 left-0 p-16">
          <p className="max-w-md font-display text-3xl font-semibold leading-tight text-white">
            Be found everywhere your customers look — search, maps, and AI answers.
          </p>
          <p className="mt-4 max-w-md text-sm text-white/70">
            Enter a website and Beacon goes to work: audit, score, and a prioritized fix list in one panel.
          </p>
        </div>
      </div>
    </div>
  );
}
